import React, { useRef, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonRouterLink,
  useIonViewWillLeave,
} from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import { documentOutline } from 'ionicons/icons';
import {
  useContactState,
  parseVCardFile,
  useImportToast,
} from '@/modules/contact';
import SnowballMenu from '@/modules/user/SnowballMenu';

const ContactsImportSettings: React.FC = () => {
  const { dispatch } = useContactState();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [presentToast] = useAppToast();
  const presentImportToast = useImportToast();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.vcf')) {
      presentToast({
        message: 'Please select a .vcf file',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    try {
      const importedContacts = await parseVCardFile(file);
      if (importedContacts.length === 0) {
        presentToast({
          message:
            'No valid contacts found in file. Please check the file format.',
          duration: 3000,
          color: 'warning',
        });
        return;
      }
      dispatch({
        type: 'PROVISIONALLY_ADD_CONTACTS',
        payload: importedContacts,
      });
      setSelectedFile(file);
      presentImportToast(
        importedContacts.length === 1
          ? '1 contact imported'
          : `${importedContacts.length} contacts imported`
      );
    } catch {
      presentToast({
        message: 'Failed to import contacts. Please check the file format.',
        duration: 3000,
        color: 'danger',
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings/contacts" />
          </IonButtons>
          <IonTitle>Import Contacts</IonTitle>
          <IonButtons slot="end">
            <SnowballMenu />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div style={{ padding: '20px' }}>
          <IonCard>
            <IonCardContent>
              <h3>Import from VCF file</h3>
              <ol>
                <li>Switch to the Contacts app</li>
                <li>Tap on &apos;Lists&apos; in the top left corner</li>
                <li>Long press on the list you want to export</li>
                <li>Choose &apos;Export&apos;</li>
                <li>Choose &apos;Save to Files&apos;</li>
                <li>Tap on &apos;Save&apos; in the top right corner</li>
                <li>Return to the Snowball app</li>
                <li>Tap on the &apos;Import&apos; button below</li>
                <li>Select the .vcf file you just exported</li>
              </ol>
              <div
                style={{
                  background: '#f4f4f4',
                  borderRadius: '8px',
                  padding: '40px',
                  textAlign: 'center',
                  marginBottom: '20px',
                }}
              >
                <p>[Video: VCF Export Tutorial]</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".vcf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <IonButton
                expand="block"
                onClick={() => fileInputRef.current?.click()}
                color="secondary"
              >
                <IonIcon icon={documentOutline} slot="start" />
                {selectedFile ? selectedFile.name : 'Import VCF File'}
              </IonButton>
              {selectedFile && (
                <p style={{ textAlign: 'center', marginTop: '16px' }}>
                  <IonRouterLink routerLink="/ab/lists/all/contacts">
                    View imported contacts
                  </IonRouterLink>
                </p>
              )}
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ContactsImportSettings;
